import type { ProductComparativePrice } from '../../../../models/ecommerce/product/ApiProduct';
import type { A11yTextType } from '../../../common/components/A11yTextWrapper/A11yTextWrapper';
import type { EcomPromotion } from '../../../common/models/domain/ecommerce/promotion/promotion';
import type { PriceData, PriceType } from '../../../common/models/priceData/priceData';

import { Helpers } from '../../../../utility/helpers';
import { getPiecePriceForHigherThanHistoricalPrice } from '../../../cart/components/molecules/PrepurchaseProductPriceLabels/PrepurchaseProductPriceLabels.utils';
import { pickProductPricePromotion } from '../../../common/components/atoms/ProductPrice/ProductPricePromotionPicker';
import { EcomProductPrice } from '../EcomProductPrice';
import { ProductInfoProperty } from '../ProductInfoProperty';
import {
    getExcludingTaxText,
    getHistoricalPriceText,
    getProductComparativePriceText,
    getProductMaxPromoUsageText,
    getProductOriginalPriceText,
    getProductRecycleFeeText,
} from '../ProductInfoProperty/ProductInfoProperty.utils';

/**
 * DEFINITIONS:
 *
 * LABEL - a part of information that is visually isolated by i.e. having a different background or paddings. Contains information how to render and what a11y text to use.
 * PRESENTATION SPEC (or simply SPEC)  - a string constant that is a specification of a single LABEL
 * SPEC SET - a set of PRESENTATION SPECs that determine exact order of LABEL rendering and what exactly will be rendered (or read out in screen reader).
 *
 * The goal of this structure is to have a single source of truth for the order of labels and their a11y text that is STRONGLY TYPED - when adding a new label it will be impossible to forget to add A11Y text because of TS errors
 */

export type ProductPresentationLabel = {
    a11y: string | undefined;
    render: () => React.ReactNode;
};

// Contains all the possible product specs that CAN be visible, but does not guarantee they WILL be visible - that depends on the existance of data and is checked inside getProductPriceLabelForSpec function
const productPricePresentationSpecSet = [
    'maxPromoUsage',
    'recycleFee',
    'promoComparativePrice',
    'originalPrice',
    'comparativePrice',
    'excludingTax',
    'historicalPrice',
    /** The price customer will pay per piece */
    'primaryPrice',
] as const;

export type ProductPricePresentationSpec = (typeof productPricePresentationSpecSet)[number];

export const isProductPricePresentationSpec = (
    spec: string,
): spec is ProductPricePresentationSpec => {
    return productPricePresentationSpecSet.includes(spec);
};

export type ProductPricePresentationData = {
    priceData: PriceData | undefined;
    promotion: EcomPromotion | undefined;
    recycleFeeData: PriceData | undefined;
    historicalPriceData: PriceData | undefined;
    comparativePrice: ProductComparativePrice | undefined;
    promotionPriceData: PriceData | undefined;
};

export type ProductPriceLabelForSpecParams = {
    product: ProductPricePresentationData & { displayLowerPrice: boolean };
    priceType: PriceType;
    a11yType: A11yTextType;
    isUserMember: boolean;
};

export const getProductPriceLabelForSpec =
    ({ product, priceType, a11yType, isUserMember }: ProductPriceLabelForSpecParams) =>
    (spec: ProductPricePresentationSpec): ProductPresentationLabel | null => {
        switch (spec) {
            case 'maxPromoUsage': {
                const texts = getProductMaxPromoUsageText(product);
                if (!texts?.visual) return null;

                return {
                    a11y: texts.a11y,
                    render: () => (
                        <ProductInfoProperty.Red a11yText={texts.a11y} a11yType={a11yType}>
                            {texts.visual}
                        </ProductInfoProperty.Red>
                    ),
                };
            }
            case 'recycleFee': {
                const texts = getProductRecycleFeeText(product, priceType);
                if (!texts?.visual) return null;

                return {
                    a11y: texts.a11y,
                    render: () => (
                        <ProductInfoProperty.Black a11yText={texts.a11y} a11yType={a11yType}>
                            {texts.visual}
                        </ProductInfoProperty.Black>
                    ),
                };
            }

            case 'originalPrice': {
                const texts = getProductOriginalPriceText(product, priceType);
                if (!texts?.visual) return null;

                return {
                    a11y: texts.a11y,
                    render: () => (
                        <ProductInfoProperty.Black a11yText={texts.a11y} a11yType={a11yType}>
                            {texts.visual}
                        </ProductInfoProperty.Black>
                    ),
                };
            }
            case 'comparativePrice': {
                const texts =
                    product.comparativePrice &&
                    getProductComparativePriceText({
                        priceData: product.comparativePrice.priceData,
                        unit: product.comparativePrice.unit,
                        postfix: product.comparativePrice.postfix,
                        fallbackText: product.comparativePrice.fallbackText,
                        priceType,
                        basedOn: product.promotion ? 'regular' : 'default',
                    });
                if (!texts?.visual) return null;

                return {
                    a11y: texts.a11y,
                    render: () => (
                        <ProductInfoProperty.Black a11yText={texts.a11y} a11yType={a11yType}>
                            {texts.visual}
                        </ProductInfoProperty.Black>
                    ),
                };
            }
            case 'promoComparativePrice': {
                // EDLP uses plain "Jfr pris". Campaigns keep "Erbj. jfr pris".
                const picked = pickProductPricePromotion({
                    promo: product.promotion,
                    checkUserMemberContext: false,
                });

                const texts =
                    product.promotion?.comparativePriceData &&
                    product.comparativePrice &&
                    getProductComparativePriceText({
                        priceData: product.promotion.comparativePriceData,
                        unit: product.comparativePrice.unit,
                        postfix: product.comparativePrice.postfix,
                        fallbackText: product.comparativePrice.fallbackText,
                        priceType,
                        basedOn: picked.type === 'edlp' ? 'default' : 'promotion',
                    });
                if (!texts?.visual) return null;

                return {
                    a11y: texts.a11y,
                    render: () => (
                        <ProductInfoProperty.Black a11yText={texts.a11y} a11yType={a11yType}>
                            {texts.visual}
                        </ProductInfoProperty.Black>
                    ),
                };
            }
            case 'historicalPrice': {
                const texts = getHistoricalPriceText(product, priceType);
                if (!texts?.visual) return null;

                return {
                    a11y: texts.a11y,
                    render: () => (
                        <ProductInfoProperty.Black a11yText={texts.a11y} a11yType={a11yType}>
                            {texts.visual}
                        </ProductInfoProperty.Black>
                    ),
                };
            }
            case 'excludingTax': {
                const texts = getExcludingTaxText(priceType);
                if (!texts?.visual) return null;

                return {
                    a11y: texts.a11y,
                    render: () => (
                        <ProductInfoProperty.Tag
                            theme="neutral"
                            a11yText={texts.a11y}
                            a11yType={a11yType}
                        >
                            {texts.visual}
                        </ProductInfoProperty.Tag>
                    ),
                };
            }
            case 'primaryPrice': {
                const picked = pickProductPricePromotion({
                    promo: product.promotion,
                    checkUserMemberContext: false,
                });

                let texts;
                switch (picked.type) {
                    /* Show original price ONLY when any promotion is applicable */
                    case 'fixed': {
                        if (picked.promotion.higherThanHistoricalPrice) {
                            const finalPiecePrice = getPiecePriceForHigherThanHistoricalPrice({
                                isMemberPrice: picked.promotion.isMemberPrice,
                                newPiecePrice: picked.promotion.fixedPiecePrice,
                                piecePrice: product.priceData,
                                isUserMember,
                            });

                            texts = EcomProductPrice.getRegularPriceTexts(
                                finalPiecePrice,
                                priceType,
                                product.displayLowerPrice,
                            );
                        } else if (picked.promotion.fixedPiecePrice) {
                            texts = EcomProductPrice.getFlatDiscountPriceTexts(
                                picked.promotion.fixedPiecePrice,
                                priceType,
                            );
                        }

                        break;
                    }
                    case 'percentage': {
                        if (picked.promotion.higherThanHistoricalPrice) {
                            const finalPiecePrice = getPiecePriceForHigherThanHistoricalPrice({
                                isMemberPrice: picked.promotion.isMemberPrice,
                                newPiecePrice: product.promotionPriceData,
                                piecePrice: product.priceData,
                                isUserMember,
                            });

                            texts = EcomProductPrice.getRegularPriceTexts(
                                finalPiecePrice,
                                priceType,
                                product.displayLowerPrice,
                            );
                        } else {
                            texts = EcomProductPrice.getPercentagePromotionTexts({
                                isMemberPrice: picked.promotion.isMemberPrice,
                                percentage: picked.promotion.percentageDiscount,
                                newPiecePrice: product.promotionPriceData,
                                priceType,
                            });
                        }
                        break;
                    }
                    case 'multiBuy':
                        texts = EcomProductPrice.getMultiBuyPromotionTexts(
                            picked.promotion,
                            priceType,
                        );
                        break;
                    case 'edlp': {
                        texts = EcomProductPrice.getEdlpPriceTexts(picked.promotion, priceType);
                        break;
                    }
                    case 'notApplicable': {
                        texts = EcomProductPrice.getRegularPriceTexts(
                            product.priceData,
                            priceType,
                            product.displayLowerPrice,
                        );
                        break;
                    }
                    default:
                        if (product.promotionPriceData) {
                            texts = EcomProductPrice.getFlatDiscountPriceTexts(
                                product.promotionPriceData,
                                priceType,
                            );
                        } else {
                            texts = EcomProductPrice.getRegularPriceTexts(
                                product.priceData,
                                priceType,
                                product.displayLowerPrice,
                            );
                        }
                }

                const { a11yText, visualText } = texts || {};
                if (!visualText) {
                    return null;
                }

                return {
                    a11y: a11yText,
                    render: () => (
                        <ProductInfoProperty.Black a11yText={a11yText} a11yType={a11yType}>
                            {`${visualText} kr/st`}
                        </ProductInfoProperty.Black>
                    ),
                };
            }
            default:
                return Helpers.exhaustiveCheck(spec);
        }
    };
